import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { DashboardLayout } from "@/components/dashboard/DashboardLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { TrendingUp, DollarSign, CheckCircle, AlertCircle, Sparkles } from "lucide-react";
import { useState } from "react";

export default function MonetizationHub() {
  const { user } = useAuth();
  const [followers, setFollowers] = useState("");
  const [engagementRate, setEngagementRate] = useState("");

  const { data: athleteProfile } = useQuery({
    queryKey: ["athlete-profile", user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("athlete_profiles")
        .select("*, athletes(*)")
        .eq("user_id", user?.id)
        .single();

      if (error) throw error;
      return data;
    },
    enabled: !!user,
  });

  const { data: myDeals } = useQuery({
    queryKey: ["my-deals", athleteProfile?.athlete_id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("deals")
        .select("*")
        .eq("athlete_id", athleteProfile?.athlete_id)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data;
    },
    enabled: !!athleteProfile?.athlete_id,
  });

  const { data: opportunities } = useQuery({
    queryKey: ["matched-opportunities", athleteProfile?.athletes?.sport],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("brand_registry")
        .select("*")
        .eq("partner_tier", "marketplace")
        .not("opportunity_posts", "is", null);

      if (error) throw error;

      // Filter opportunities by athlete's sport
      const allOpportunities: any[] = [];
      data.forEach((brand) => {
        const posts = brand.opportunity_posts as any[];
        if (posts && Array.isArray(posts)) {
          posts
            .filter((post) => post.sport === athleteProfile?.athletes?.sport)
            .forEach((post) => {
              allOpportunities.push({
                ...post,
                brandName: brand.primary_name,
                brandLogo: brand.logo_url,
              });
            });
        }
      });

      return allOpportunities.slice(0, 6);
    },
    enabled: !!athleteProfile?.athletes?.sport,
  });

  const calculateMarketRate = () => {
    const followerCount = parseInt(followers) || 0;
    const engagement = parseFloat(engagementRate) || 0;

    if (followerCount === 0) return { min: 0, max: 0 };

    const baseRate = followerCount * 0.01;
    const engagementMultiplier = 1 + engagement / 100;
    const min = Math.floor(baseRate * engagementMultiplier * 0.8);
    const max = Math.floor(baseRate * engagementMultiplier * 1.5);

    return { min, max };
  };

  const marketRate = calculateMarketRate();

  const getDealStatusIcon = (status: string) => {
    switch (status) {
      case "active":
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case "pending_compliance":
      case "pending_review":
        return <AlertCircle className="h-4 w-4 text-yellow-500" />;
      default:
        return null;
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold">Monetization Hub</h1>
          <p className="text-muted-foreground">
            Discover opportunities and track your NIL earnings
          </p>
        </div>

        <div className="grid gap-6 md:grid-cols-3">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Earnings</CardTitle>
              <DollarSign className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                $
                {myDeals
                  ?.filter((d) => d.status === "active")
                  .reduce((sum, d) => sum + Number(d.amount), 0)
                  .toLocaleString()}
              </div>
              <p className="text-xs text-muted-foreground">From active deals</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Active Deals</CardTitle>
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {myDeals?.filter((d) => d.status === "active").length || 0}
              </div>
              <p className="text-xs text-muted-foreground">Currently active</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Matched Opportunities
              </CardTitle>
              <Sparkles className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{opportunities?.length || 0}</div>
              <p className="text-xs text-muted-foreground">Based on your profile</p>
            </CardContent>
          </Card>
        </div>

        <Tabs defaultValue="opportunities" className="space-y-4">
          <TabsList>
            <TabsTrigger value="opportunities">Opportunities for You</TabsTrigger>
            <TabsTrigger value="calculator">Market Rate Calculator</TabsTrigger>
            <TabsTrigger value="timeline">Deal Timeline</TabsTrigger>
          </TabsList>

          <TabsContent value="opportunities" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>AI-Matched Opportunities</CardTitle>
                <CardDescription>
                  Deals curated for {athleteProfile?.athletes?.name}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid gap-4 md:grid-cols-2">
                  {opportunities?.map((opp, index) => (
                    <Card key={index}>
                      <CardHeader>
                        <div className="flex items-center gap-2">
                          {opp.brandLogo && (
                            <img
                              src={opp.brandLogo}
                              alt={opp.brandName}
                              className="h-8 w-8 rounded-full"
                            />
                          )}
                          <div>
                            <CardTitle className="text-base">{opp.title}</CardTitle>
                            <CardDescription>{opp.brandName}</CardDescription>
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        <p className="text-sm text-muted-foreground line-clamp-2">
                          {opp.description}
                        </p>
                        <div className="flex items-center justify-between">
                          <Badge variant="secondary">{opp.category}</Badge>
                          <span className="font-semibold">
                            ${opp.compensation?.toLocaleString()}
                          </span>
                        </div>
                        <Button size="sm" className="w-full">
                          Apply Now
                        </Button>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="calculator" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Market Rate Calculator</CardTitle>
                <CardDescription>
                  Estimate your value based on social media metrics
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <Label htmlFor="followers">Total Followers</Label>
                    <Input
                      id="followers"
                      type="number"
                      placeholder="e.g., 50000"
                      value={followers}
                      onChange={(e) => setFollowers(e.target.value)}
                    />
                  </div>
                  <div>
                    <Label htmlFor="engagement">Engagement Rate (%)</Label>
                    <Input
                      id="engagement"
                      type="number"
                      step="0.1"
                      placeholder="e.g., 3.5"
                      value={engagementRate}
                      onChange={(e) => setEngagementRate(e.target.value)}
                    />
                  </div>
                </div>

                {marketRate.max > 0 && (
                  <div className="p-6 bg-primary/10 rounded-lg">
                    <h3 className="font-semibold mb-2">Estimated Value Per Post</h3>
                    <div className="text-3xl font-bold">
                      ${marketRate.min.toLocaleString()} - $
                      {marketRate.max.toLocaleString()}
                    </div>
                    <p className="text-sm text-muted-foreground mt-2">
                      Based on athletes with similar metrics in{" "}
                      {athleteProfile?.athletes?.sport}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="timeline" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Your Deal Timeline</CardTitle>
                <CardDescription>Track all submitted deals</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {myDeals?.map((deal) => (
                    <div
                      key={deal.id}
                      className="flex items-center justify-between p-4 border rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        {getDealStatusIcon(deal.status)}
                        <div>
                          <div className="font-semibold">{deal.title}</div>
                          <div className="text-sm text-muted-foreground">
                            {deal.brand_name}
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-semibold">
                          ${Number(deal.amount).toLocaleString()}
                        </div>
                        <Badge variant="outline">{deal.status}</Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}
